<template>
  <v-container fluid style="height: 100vh; max-width: 600px">
    <v-layout column fill-height>
      <v-row class="flex-grow-0">
        <v-toolbar dense elevation="0">
          <v-icon class="pr-1" dense>mdi-crosshairs-gps</v-icon>
          <v-btn rounded text style="font-size: 110%; padding: 0 5px" @click="selectVehicle">{{ vehicle.numericPlate
            }}
          </v-btn>
          <v-spacer></v-spacer>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionHistory" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>mdi-history</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.history") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionLogbook" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>mdi-text-box-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.logbook") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionRefueling" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>local_gas_station</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.refueling") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionEvents" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>event_available</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.events") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionService" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>build</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.service") }}</span>
          </v-tooltip>

          <v-tooltip bottom open-delay="500" v-if="canRead('contract')">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="actionContracts" v-bind="attrs" v-on="on" small class="ma-1">
                <v-icon>widgets</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.contracts") }}</span>
          </v-tooltip>

          <template v-if="accessRole('dispatcher')">
            <v-menu offset-y z-index="4000">
              <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom open-delay="500">
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      small
                      icon
                      class="ml-1"
                      v-bind="attrs"
                      v-on="{ ...tooltip, ...menu }"
                    >
                      <v-icon>more_vert</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("map.panel.online.tooltip.more") }}</span>
                </v-tooltip>
              </template>
              <v-list>
                <v-list-item link @click="actionUpdate">
                  <v-list-item-icon style="margin-right: 10px">
                    <v-icon>edit_square</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("map.panel.online.tooltip.update") }}</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="$store.state.auth.token.superuser" link @click="actionEdit">
                  <v-list-item-icon style="margin-right: 10px">
                    <v-icon>edit</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("map.panel.online.tooltip.edit") }}</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="$store.state.auth.token.superuser" link @click="actionLog">
                  <v-list-item-icon style="margin-right: 10px">
                    <v-icon>assignment</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ $t("map.panel.online.tooltip.log") }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>

          <v-divider vertical class="my-2 ml-1" inset />

          <v-tooltip bottom open-delay="500">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon @click="closeVehicle" v-bind="attrs" v-on="on">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("map.panel.tooltip.close") }}</span>
          </v-tooltip>

        </v-toolbar>
      </v-row>
      <v-row class="overflow-y-auto" style="max-height: 100%">
        <v-container class="px-4">
          <TextRow :title="$t('map.panel.vehicle.base.name')" :text="vehicle.name" />
          <TextRow :title="$t('map.panel.vehicle.base.type')" :text="vehicle.typeCode" enum-code="vehicle_type" />
          <TextRow :title="$t('map.panel.vehicle.base.location')" :text="formatPlace(vehicle.location)"
                   :flag="getCountryCode(vehicle.location)" />
          <TextRow :title="$t('map.panel.vehicle.base.timestamp')" :text="formatTime(vehicle.location)" />
          <TextRow :title="$t('map.panel.vehicle.base.speed')" :text="formatSpeed(vehicle.location)" />
          <TextRow v-if="vehicle.typeCode === 'machine'" :title="$t('map.panel.vehicle.base.enginehours')"
                   :text="formatEngineHoursState(vehicle)" />
          <TextRow v-else :title="$t('map.panel.vehicle.base.odometer')" :text="formatOdometer(vehicle)" />
          <TextRow :title="$t('map.panel.vehicle.base.driver_quality')" v-if="vehicle.location.acceleration !== null">
            <v-icon small>workspace_premium</v-icon>
            <span :class="[classQuality(locationDriverQuality()), 'font-weight-bold']"
                  style="padding-left: 4px">{{ formatQuality(locationDriverQuality()) }}</span>
          </TextRow>
          <TextRow :title="$t('map.panel.vehicle.base.coords')">
            {{ formatCoords(vehicle.location) }}
            <v-tooltip style="z-index: 9999" bottom open-delay="500">
              <template v-slot:activator="{ on }">
                <v-btn icon x-small class="pl-1" target="_blank" :href="hrefGoogle(vehicle.location)" v-on="on">
                  <v-icon>public</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("map.panel.vehicle.base.coords_google_map") }}</span>
            </v-tooltip>
            <!--
            <v-tooltip style="z-index: 9999" bottom open-delay="500">
              <template v-slot:activator="{ on }">
                <v-btn icon x-small class="pl-1" v-on:click="hrefGoogleToClipboard(vehicle.location)" v-on="on">
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("map.panel.vehicle.base.coords_google_clipboard") }}</span>
            </v-tooltip> -->
          </TextRow>
          <TextRow v-if="getLastMeasure('vcc') !== undefined"
                   :title="$t('map.panel.vehicle.base.vcc')" :text="formatVcc(getLastMeasure('vcc').measureValue)" />
          <TextRow v-if="getLastMeasure('temp1') !== undefined"
                   :title="$t('map.panel.vehicle.base.temp1')"
                   :text="formatTemperature(getLastMeasure('temp1').measureValue)" />
          <TextRow v-if="getLastMeasure('temp2') !== undefined"
                   :title="$t('map.panel.vehicle.base.temp2')"
                   :text="formatTemperature(getLastMeasure('temp2').measureValue)" />

          <ExpansionPanel :title="$t('map.panel.vehicle.info.title')" style="margin-top: 14px">
            <TextRow :title="$t('map.panel.vehicle.info.numeric_plate')" :text="vehicle.numericPlate" />
            <TextRow :title="$t('map.panel.vehicle.info.vin')" :text="vehicle.vin" />
            <TextRow :title="$t('map.panel.vehicle.info.manufacture_year')" :text="vehicle.manufactureYear" />
            <TextRow :title="$t('map.panel.vehicle.info.enorm')" :text="vehicle.euroNormCode"
                     enum-code="vehicle_norm" />
            <TextRow :title="$t('map.panel.vehicle.info.category')" :text="vehicle.categoryCode"
                     enum-code="vehicle_category" />
            <TextRow :title="$t('map.panel.vehicle.info.type_design')" :text="vehicle.typeDesign" />
            <TextRow :title="$t('map.panel.vehicle.info.device_code')" :text="vehicle.device.code"
                     v-if="$store.state.auth.token.superuser" />
            <TextRow :title="$t('map.panel.vehicle.info.id')" :text="vehicle.vehicleId"
                     v-if="$store.state.auth.token.superuser" />
          </ExpansionPanel>

          <ExpansionPanel v-if="servicePlans.length > 0" :title="$t('map.panel.vehicle.service-plans.title')" expanded>
            <TextRow v-for="item in servicePlans" v-bind:key="item.servicePLanId"
                     :title="formatServicePlanName(item)">
              <span v-html="formatServicePlanNext(item)" />
            </TextRow>
          </ExpansionPanel>

          <ExpansionPanel
            :title="vehicle.ride.rideState === 'unfinished' ? $t('map.panel.vehicle.ride.active_title') : $t('map.panel.vehicle.ride.last_title')"
            expanded v-if="vehicle.ride !== null">
            <TextRow :title="$t('map.panel.vehicle.ride.type')" :text="vehicle.ride.rideType" enum-code="ride_type" />
            <TextRow :title="$t('map.panel.vehicle.ride.state')" :text="vehicle.ride.rideState" enum-code="ride_state"
                     :text-class="vehicle.ride.rideState === 'unfinished' ? 'blue--text' : 'null'" />
            <TextRow :title="$t('map.panel.vehicle.ride.from')" :text="formatPlace(vehicle.ride.locationFrom)"
                     :flag="getCountryCode(vehicle.ride.locationFrom)" />
            <TextRow title="" :text="getPlaceName(vehicle.ride.locationFrom)"
                     v-if="getPlaceName(vehicle.ride.locationFrom) != null" />
            <TextRow title="" :text="formatTime(vehicle.ride.locationFrom)" />
            <TextRow :title="$t('map.panel.vehicle.ride.to')" :text="formatPlace(vehicle.ride.locationTo)"
                     :flag="getCountryCode(vehicle.ride.locationTo)" />
            <TextRow title="" :text="getPlaceName(vehicle.ride.locationTo)"
                     v-if="getPlaceName(vehicle.ride.locationTo) != null" />
            <TextRow title="">
              {{ formatTime(vehicle.ride.locationTo) }}
              <span class="grey--text">&nbsp;•&nbsp;</span>
              <span class="blue--text">{{ formatRideDuration(vehicle.ride.locationFrom, vehicle.ride.locationTo)
                }}</span>
            </TextRow>
            <TextRow v-if="vehicle.typeCode === 'machine'" :title="$t('map.panel.vehicle.ride.enginehours')"
                     :text="formatEngineHours(vehicle.ride.engineHours)" />
            <TextRow v-else :title="$t('map.panel.vehicle.ride.distance')"
                     :text="formatDistance(vehicle.ride.distance)" />
            <TextRow :title="$t('map.panel.vehicle.ride.driver_quality')" v-if="vehicle.ride.driverQuality !== null">
              <v-icon small>workspace_premium</v-icon>
              <span :class="[classQuality(vehicle.ride.driverQuality), 'font-weight-bold']"
                    style="padding-left: 4px">{{ formatQuality(vehicle.ride.driverQuality) }}</span>
            </TextRow>
            <TextRow :title="$t('map.panel.vehicle.ride.elevation')">
              <v-icon small color="blue">mdi-arrow-top-right</v-icon>
              {{ formatElevation(vehicle.ride.climb) }}
              &nbsp;
              <v-icon small color="red">mdi-arrow-bottom-right</v-icon>
              {{ formatElevation(vehicle.ride.descent) }}
            </TextRow>
            <TextRow v-if="vehicle.ride.consumptionDiff !== null && vehicle.ride.consumptionDiff > 0"
                     :title="$t('map.panel.vehicle.ride.consumption')">
              {{ formatConsumption(vehicle.ride.consumptionDiff) }}
              <span v-if="showAverageConsumption(vehicle)">
                <span class="grey--text">&nbsp;•&nbsp;</span>
                <span class="blue--text">{{ formatAverageConsumption(vehicle.ride.consumptionDiff) }}</span>
              </span>
            </TextRow>
            <TextRow v-else-if="vehicle.ride.tankDiff !== null && vehicle.ride.tankDiff > 0"
                     :title="$t('map.panel.vehicle.ride.consumption')">
              {{ formatConsumption(vehicle.ride.tankDiff) }}
              <span v-if="showAverageConsumption(vehicle)">
                <span class="grey--text">&nbsp;•&nbsp;</span>
                <span class="blue--text">{{ formatAverageConsumption(vehicle.ride.tankDiff) }}</span>
              </span>
            </TextRow>
            <TextRow :title="$t('map.panel.vehicle.ride.max_speed')" :text="formatMaxSpeed(vehicle.ride.maxSpeed)" />
            <TextRow v-if="vehicle.maxSpeed > 0"
                     :title="$t('map.panel.vehicle.ride.over_speed')"
                     :text="formatSecondsDuration(vehicle.ride.overSpeed)" />
            <TextRow v-if="vehicle.ride.motorIdle > 0"
                     :title="$t('map.panel.vehicle.ride.motor_idle')"
                     :text="formatSecondsDuration(vehicle.ride.motorIdle)" />
            <TextRow v-if="vehicle.ride.axleLoad > 0"
                     :title="$t('map.panel.vehicle.ride.axle_load')" :text="formatWeight(vehicle.ride.axleLoad)" />
            <TextRow v-if="vehicle.ride.vcc > 0"
                     :title="$t('map.panel.vehicle.ride.vcc')" :text="formatVcc(vehicle.ride.vcc)" />
            <TextRow :title="$t('map.panel.vehicle.ride.driver')"
                     :text="vehicle.ride.driver.firstName + ' ' + vehicle.ride.driver.lastName"
                     v-if="vehicle.ride.driver !== null" />
            <TextRow :title="$t('map.panel.vehicle.ride.note')"
                     :text="vehicle.ride.note"
                     v-if="vehicle.ride.note !== null" />
            <ProgressRow :title="$t('map.panel.vehicle.ride.tanks')"
                         :text="formatTankLevel(vehicle)" color="green" :value="vehicle.ride.tankSum"
                         :max-value="getTanksCapacity(vehicle)"
                         v-if="vehicle.ride.tankSum && vehicle.tanks !== null && vehicle.tanks.length > 0" />
          </ExpansionPanel>
          <ExpansionPanel :title="$t('map.panel.vehicle.tanks.title')"
                          v-if="vehicle.tanks !== null && vehicle.tanks.length > 0 && vehicle.ride !== null && vehicle.ride.tank !== null">
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank1')" :text="formatTankLevelIndex(vehicle, 0)"
                         color="green"
                         :value="vehicle.ride.tank[0]"
                         :max-value="vehicle.tanks[0].capacity" v-if="validTank(vehicle, 0)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank2')" :text="formatTankLevelIndex(vehicle, 1)"
                         color="green"
                         :value="vehicle.ride.tank[1]"
                         :max-value="vehicle.tanks[1].capacity" v-if="validTank(vehicle, 1)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank3')" :text="formatTankLevelIndex(vehicle, 2)"
                         color="green"
                         :value="vehicle.ride.tank[2]"
                         :max-value="vehicle.tanks[2].capacity" v-if="validTank(vehicle, 2)" />
            <ProgressRow :title="$t('map.panel.vehicle.tanks.tank4')" :text="formatTankLevelIndex(vehicle, 3)"
                         color="green"
                         :value="vehicle.ride.tank[3]"
                         :max-value="vehicle.tanks[3].capacity" v-if="validTank(vehicle, 3)" />
          </ExpansionPanel>
          <ExpansionPanel :title="$t('map.panel.vehicle.axleweight.title')"
                          v-if="vehicle.axles !== null && vehicle.axles.length > 0 && vehicle.ride !== null && vehicle.ride.axleWeight !== null">
            <table class="table-axle-weigh"
                   style="margin-left: auto; margin-right: auto;margin-top: -12px;margin-bottom: -10px;">
              <tbody>
              <tr>
                <template v-for="(item, index) in axles(vehicle.axles, vehicle.ride.axleWeight)">
                  <td :key="index">{{ item.symbol }}</td>
                </template>
              </tr>
              <tr>
                <template v-for="(item, index) in axles(vehicle.axles, vehicle.ride.axleWeight)">
                  <td :key="index" class="axle-small">{{ item.weight }}</td>
                </template>
              </tr>
              </tbody>
            </table>
          </ExpansionPanel>
          <ExpansionPanel expanded
                          v-if="vehicle.ride != null && vehicle.ride.driver && vehicle.ride.driver.tachograph">
            <template #title>
              {{ $t("map.panel.vehicle.tachograph.title") }}
              <span v-if="!vehicle.ride.driver.tachograph.dataValidity" class="font-weight-bold red--text">&nbsp;&nbsp;{{ $t("map.panel.vehicle.noactive_suffix")
                }}</span>
              <v-spacer />
              <v-tooltip style="z-index: 9999" bottom open-delay="500">
                <template v-slot:activator="{ on }">
                  <v-btn color="primary" x-small elevation="0" style="max-width: 80px" class="mr-5"
                         @click="reportTachograph" v-on="on">
                    {{ $t("map.panel.vehicle.tachograph.history") }}
                  </v-btn>
                </template>
                <span>{{ $t("map.panel.vehicle.tachograph.history_tooltip") }}</span>
              </v-tooltip>
            </template>
            <TextRow :title="$t('map.panel.vehicle.tachograph.card_id')"
                     :text="vehicle.ride.driver.tachograph.cardId" />
            <TextRow :title="$t('map.panel.vehicle.tachograph.last_update')">
              <span>{{ formatUtcTime(vehicle.ride.driver.tachograph.lastTimestamp) }}</span>
              <span v-if="!vehicle.ride.driver.tachograph.dataValidity" class="font-weight-bold red--text">&nbsp;&nbsp;{{ $t("map.panel.vehicle.noactive_suffix")
                }}</span>
            </TextRow>
            <TextRow :title="$t('map.panel.vehicle.tachograph.activity')">
              <v-icon small style="padding-bottom: 2px">
                {{ tachographActivityIcon(vehicle.ride.driver.tachograph.activity) }}
              </v-icon>&nbsp;
              {{ $t("enum.tachograph_activity." + vehicle.ride.driver.tachograph.activity) }}
            </TextRow>
            <ProgressRow :title="$t('map.panel.vehicle.tachograph.day_drive')"
                         :value="vehicle.ride.driver.tachograph.dayDriveAccumulation"
                         :max-value="vehicle.ride.driver.tachograph.dayDriveLimit"
                         color="orange">
              {{ formatDuration(vehicle.ride.driver.tachograph.dayDriveAccumulation) }}
              <span class="grey--text text--darken-1">&nbsp;•&nbsp;{{ $t("common.remaining") }}&nbsp;</span>
              {{ formatDuration(vehicle.ride.driver.tachograph.dayDriveLimit
              - vehicle.ride.driver.tachograph.dayDriveAccumulation) }}
            </ProgressRow>
            <TextRow :title="$t('map.panel.vehicle.tachograph.day_rest')"
                     :text="formatUtcTimeDuration(vehicle.ride.driver.tachograph.lastDayRestTime, vehicle.ride.driver.tachograph.lastDayRestDuration)" />
            <TextRow title=""
                     v-if="vehicle.ride.driver.tachograph.lastDayRestTime">
              {{ formatUtcTimeDuration(vehicle.ride.driver.tachograph.lastDayRestTime, vehicle.ride.driver.tachograph.lastDayRestDuration, false)
              }}
              <span class="grey--text">&nbsp;•&nbsp;</span>
              <span
                class="blue--text">{{ formatDuration(vehicle.ride.driver.tachograph.lastDayRestDuration)
                }}</span>
            </TextRow>
            <ProgressRow :title="$t('map.panel.vehicle.tachograph.week_drive')"
                         :value="vehicle.ride.driver.tachograph.weekDriveAccumulation"
                         :max-value="vehicle.ride.driver.tachograph.weekDriveLimit"
                         color="blue">
              {{ formatDuration(vehicle.ride.driver.tachograph.weekDriveAccumulation) }}
              <span class="grey--text text--darken-1">&nbsp;•&nbsp;{{ $t("common.remaining") }}&nbsp;</span>
              {{ formatDuration(vehicle.ride.driver.tachograph.weekDriveLimit
              - vehicle.ride.driver.tachograph.weekDriveAccumulation) }}
            </ProgressRow>
            <TextRow :title="$t('map.panel.vehicle.tachograph.week_rest')"
                     :text="formatUtcTimeDuration(vehicle.ride.driver.tachograph.lastWeekRestTime, vehicle.ride.driver.tachograph.lastWeekRestDuration)" />
            <TextRow title=""
                     v-if="vehicle.ride.driver.tachograph.lastWeekRestTime">
              {{ formatUtcTimeDuration(vehicle.ride.driver.tachograph.lastWeekRestTime, vehicle.ride.driver.tachograph.lastWeekRestDuration, false)
              }}
              <span class="grey--text">&nbsp;•&nbsp;</span>
              <span
                class="blue--text">{{ formatDuration(vehicle.ride.driver.tachograph.lastWeekRestDuration)
                }}</span>
            </TextRow>
            <TextRow :title="$t('map.panel.vehicle.tachograph.twoweeks_drive')"
                     :text="formatDuration((vehicle.ride.driver.tachograph.previousWeekDriveAccumulation || 0) + (vehicle.ride.driver.tachograph.weekDriveAccumulation || 0))" />
          </ExpansionPanel>
        </v-container>
      </v-row>
    </v-layout>
  </v-container>
</template>

<script>
import TextRow from "@/components/properties/TextRow";
import ProgressRow from "@/components/properties/ProgressRow";
import ExpansionPanel from "@/components/properties/ExpansionPanel";
import utils from "@/utils";
import { DateTime } from "luxon";

export default {
  name: "SideVehicle",
  components: { ExpansionPanel, ProgressRow, TextRow },
  props: {
    vehicleSidebarClick: {}
  },
  data() {
    return {
      panels: []
    };
  },
  methods: {
    closeVehicle() {
      this.$store.dispatch("online/setVehicle", null);
      this.$router.routeToMap();
    },

    selectVehicle() {
      this.$store.dispatch("dialogs/showDialog", {
        dialog: "vehicleSelection", options: true
      });
    },

    actionHistory() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/history",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionLogbook() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/logbook",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionEvents() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/events",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionContracts() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/contracts",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionService() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/service",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionRefueling() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "map/refueling",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionUpdate() {
      this.$store.dispatch("dialogs/showDialog", { dialog: "vehicleUpdate", options: this.vehicle });
    },

    actionEdit() {
      let vehicleId = this.vehicle.vehicleId;
      this.$router.replace({
        name: "data/vehicle",
        params: { ccode: this.$store.state.auth.company.code, id: vehicleId }
      });
    },

    actionLog() {
      this.$store.dispatch("dialogs/showDialog", {
        dialog: "vehicleLog", options:
          { vehicle: this.vehicle }
      });
    },

    getLastMeasure(code) {
      if (this.vehicle.lastMeasures !== null) {
        return this.vehicle.lastMeasures
          .find(x => x.parameter !== null && x.parameter.code === code);
      } else
        return undefined;
    },

    reportTachograph() {
      if (this.vehicle.ride !== null && this.vehicle.ride.driver !== null) {
        let personId = this.vehicle.ride.driver.personId;
        this.$router.push({
          name: "report/tachograph",
          params: { ccode: this.$store.state.auth.company.code },
          query: { "personId": personId, "periodName": "month-current" }
        });
      }
    },

    validTank(vehicle, index) {
      return (vehicle.tanks !== null && index < vehicle.tanks.length &&
        vehicle.ride !== null && vehicle.ride.tank !== null && index < vehicle.ride.tank.length);
    },

    formatOdometer(vehicle) {
      if (vehicle.ride !== null)
        return parseInt((vehicle.ride.kmStop / 1000)).toLocaleString("cs") + " km";
      else if (vehicle.kmStart > 0)
        return parseInt((vehicle.kmStart / 1000)).toLocaleString("cs") + " km";
      else
        return null;
    },

    formatEngineHoursState(vehicle) {
      if (vehicle.ride !== null)
        return parseInt((vehicle.ride.mhStop / 3600)).toLocaleString("cs") + " h";
      else if (vehicle.ehStart > 0)
        return parseInt((vehicle.ehStart / 3600)).toLocaleString("cs") + " h";
      else
        return null;
    },

    getTanksCapacity(vehicle) {
      if (vehicle.tanks !== null)
        return vehicle.tanks
          .filter(x => x.tankType === "main")
          .reduce(((sum, x) => sum + x.capacity), 0);
      else
        return 0;
    },

    formatTankLevelIndex(vehicle, index) {
      return utils.formatFuel(vehicle.ride.tank[index]);
    },

    formatTankLevel(vehicle) {
      return utils.formatFuel(vehicle.ride.tankSum);
    },

    formatDistance(distance) {
      return utils.formatDistance(distance);
    },

    formatEngineHours(enginehours) {
      return utils.formatEngineHours(enginehours);
    },

    formatElevation(elevation) {
      if (elevation >= 500)
        return utils.formatDistance(elevation);
      else
        return elevation + " m";
    },

    formatTemperature(temperature) {
      if (temperature !== null)
        return utils.numberToString(temperature, 1, true) + "°C";
      else
        return null;
    },

    formatConsumption(consumption) {
      if (consumption !== null && consumption > 0)
        return utils.numberToString(consumption) + " l";
      else
        return null;
    },

    formatCoords(location) {
      if (location !== null)
        return utils.formatCoords(location.latitude, location.longitude);
      else
        return null;
    },

    hrefGoogle(location) {
      return `https://www.google.com/maps/search/?q=${location.latitude},${location.longitude}`;
    },

    hrefGoogleToClipboard(location) {
      utils.toClipboard(this.hrefGoogle(location));
    },

    formatPlace(location) {
      if (location !== null && location.place !== null)
        return utils.formatLocation(location.place);
      else
        return null;
    },

    getCountryCode(location) {
      if (location !== null)
        return location.countryCode;
      else
        return null;
    },

    getPlaceName(location) {
      if (location !== null && location.place !== null)
        return location.place.name;
      else
        return null;
    },

    formatTime(location) {
      if (location !== null)
        return DateTime.fromISO(location.timestamp).toLocaleString(DateTime.DATETIME_SHORT);
      else
        return null;
    },

    formatRideDuration(from, to) {
      if (from !== null && to !== null)
        return DateTime.fromISO(to.timestamp).diff(DateTime.fromISO(from.timestamp)).toFormat("h'h' m'm'");
      else
        return null;
    },

    formatSpeed(location) {
      if (location !== null && location.speed > 0)
        return location.speed + " km/h";
      else
        return null;
    },

    formatMaxSpeed(speed) {
      if (speed > 0)
        return speed + " km/h";
      else
        return null;
    },

    formatUtcTime(time) {
      if (time !== null)
        return DateTime.fromISO(time).toLocaleString(DateTime.DATETIME_SHORT);
      else
        return null;
    },

    formatUtcTimeDuration(time, duration, from = true) {
      if (time !== null) {
        if (from) {
          return DateTime.fromISO(time).minus({ minutes: duration }).toLocaleString(DateTime.DATETIME_SHORT);
        } else {
          return DateTime.fromISO(time).toLocaleString(DateTime.DATETIME_SHORT);
        }
      } else
        return null;
    },

    formatDuration(value) {
      if (value >= 0)
        return utils.formatHours(value);
      else
        return utils.formatHours(0);
    },

    formatSecondsDuration(value) {
      if (value >= 0)
        return utils.formatHours(value, true, false);
      else
        return utils.formatHours(0, true, false);
    },

    formatWeight(value) {
      return utils.formatWeight(value, true);
    },

    formatVcc(value) {
      return utils.formatVolts(value);
    },

    formatQuality(value) {
      return utils.numberToString(value, 1, true);
    },

    locationDriverQuality() {
      if (this.vehicle.location !== null)
        return utils.calcDriverQuality(this.vehicle.location);
      else
        return 0;
    },

    formatServicePlanName(plan) {
      if (plan.typeCode === "own")
        return plan.name;
      else if (plan.name !== null)
        return this.$t("enum.service_type." + plan.typeCode) + " - " + plan.name;
      else
        return this.$t("enum.service_type." + plan.typeCode);
    },

    formatServicePlanNext(plan) {
      const result = [];

      if (plan.diffKm !== null) {
        if (plan.diffKm <= 0)
          result.push(this.$t("common.service_is") + " " + utils.formatDistance(-plan.diffKm));
        else
          result.push("<span style=\"color: red\">" +
            this.$t("common.service_over") + " " + utils.formatDistance(plan.diffKm) + "</span>");
      }

      if (plan.diffEh !== null) {
        if (plan.diffEh <= 0)
          result.push(this.$t("common.service_is") + " " + utils.formatEngineHours(-plan.diffEh));
        else
          result.push("<span style=\"color: red\">" +
            this.$t("common.service_over") + " " + utils.formatEngineHours(plan.diffEh) + "</span>");
      }

      if (plan.diffDays !== null) {
        if (plan.diffDays <= 0)
          result.push(DateTime.fromISO(plan.date).plus({ days: 1 }).toRelative({ unit: "day" }));
        else
          result.push("<span style=\"color: red\">" +
            DateTime.fromISO(plan.date).plus({ days: 1 }).toRelative({ unit: "day" })
            + "</span>");
      }

      return this.$options.filters.joinWithSeparator(result,
        "<span style=\"font-style: italic; color: gray\"> " + this.$t("common.or") + " </span>", "-");
    },

    classQuality(value) {
      return utils.colorClassQuality(value);
    },

    tachographActivityIcon(activity) {
      return utils.tachographActivityIcon(activity);
    },

    formatAverageConsumption(diff) {
      if (this.vehicle.typeCode === "machine") {
        let acon = utils.averageEngineHoursConsumption(diff, this.vehicle.ride.engineHours, this.vehicle);

        if (acon !== null) {
          return utils.numberToString(acon) + " l/h";
        } else
          return "";
      } else {
        let acon = utils.averageDistanceConsumption(diff, this.vehicle.ride.distance, this.vehicle);

        if (acon !== null) {
          return utils.numberToString(acon) + " l/100km";
        } else
          return "";
      }
    },

    axles(axles, weight) {
      return utils.toAxleWeight(axles, weight);
    },

    showAverageConsumption(vehicle) {
      if (vehicle.typeCode === "machine")
        return vehicle.ride.engineHours >= 300;
      else
        return vehicle.ride.distance >= 500;
    }
  },

  watch: {},

  computed: {
    vehicle() {
      return this.$store.state.online.vehicle;
    },

    servicePlans() {
      if (this.vehicle.servicePlans !== undefined
        && this.vehicle.servicePlans !== null
        && this.vehicle.servicePlans.length > 0) {
        return this.vehicle.servicePlans
          .filter(x => x.diffKm !== null || x.diffEh !== null || x.diffDays !== null);
      } else
        return [];
    }
  }
};
</script>

<style lang="sass" scoped>
.service_notice
  background-color: grey
  color: white
  font-size: 90%
  padding: 2px 6px
  border-radius: 7px

.service_warning
  background-color: #c06432
  color: white
  font-size: 90%
  padding: 2px 6px
  border-radius: 7px

</style>